import React, { useMemo, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd'
import PeopleIcon from '@material-ui/icons/People'
import HotelIcon from '@material-ui/icons/Hotel'
import ListAltIcon from '@material-ui/icons/ListAlt'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import {
    useOccupancy,
    useOccupancyUnits,
} from '../../../hooks/useRenewalOffers'
import { usePersonFile } from '../PersonFile/PersonFileContext'
import { OccupancyBedBoard } from './OccupancyBedBoard'
import { OccupancyChrome } from './OccupancyChrome'
import { OccupancyAssignDialog } from './OccupancyAssignDialog'
import { OccupancyIconLegendDialog } from './OccupancyIconLegendDialog'
import { NextActionTableIcon } from '../components/NextActionMark'
import { OccupancyFactIcons } from './OccupancyFactIcons'
import { OccupancyBoardFilter, OccupancyFilter, OccupancyLease } from './types'
import {
    getLeaseType,
    getLeaseTypeLabel,
    isOccupancyImmovable,
    isUnassignedOccupancyLease,
    placementSourceLabel,
    resolveOccupancyNextAction,
} from './occupancyLease'

type ViewMode = 'leases' | 'bed_spaces'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    viewToggle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
    },
    viewToggleTabs: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    filterRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        alignItems: 'center',
    },
    viewTabs: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    search: {
        flex: '1 1 220px',
        minWidth: 220,
    },
    summaryBar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
    },
    tableCard: {
        position: 'relative',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
    },
    tableHead: {
        backgroundColor: theme.palette.grey[50],
        '& .MuiTableCell-head': {
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
        },
    },
    unassignedBadge: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 22,
        borderRadius: 6,
    },
    statusPending: {
        backgroundColor: '#ede9fe',
        color: '#6d28d9',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 22,
        borderRadius: 6,
    },
    statusFuture: {
        backgroundColor: '#dbeafe',
        color: '#1d4ed8',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 22,
        borderRadius: 6,
    },
    typeRenewal: {
        backgroundColor: '#d1fae5',
        color: '#047857',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 22,
        borderRadius: 6,
    },
    typeTransfer: {
        backgroundColor: '#fef3c7',
        color: '#b45309',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 22,
        borderRadius: 6,
    },
    typeNewMoveIn: {
        backgroundColor: '#e0e7ff',
        color: '#3730a3',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 22,
        borderRadius: 6,
    },
    occupiedChip: {
        backgroundColor: '#d1fae5',
        color: '#047857',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 22,
        borderRadius: 6,
    },
    vacantChip: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 22,
        borderRadius: 6,
    },
    clickableRow: {
        cursor: 'pointer',
    },
    personCell: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },
    personName: {
        fontWeight: 600,
    },
    personNameClickable: {
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
    expandedRow: {
        backgroundColor: '#fffbeb',
        '& > td': {
            paddingTop: 0,
            paddingBottom: 0,
            borderBottom: 'none',
        },
    },
    transferDetail: {
        padding: theme.spacing(2, 3),
        borderLeft: '3px solid #fbbf24',
    },
    transferField: {
        marginBottom: theme.spacing(1.5),
        '&:last-child': { marginBottom: 0 },
    },
    transferLabel: {
        fontSize: '0.625rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: theme.palette.text.secondary,
        marginBottom: 2,
    },
    roommateChip: {
        marginRight: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
    },
}))

const FILTER_LABELS: Record<OccupancyFilter, string> = {
    all: 'All',
    unassigned: 'Unassigned',
    drafts: 'Drafts',
    placed: 'Placed',
}

export const OccupancyHome: React.FC = () => {
    const classes = useStyles()
    const { leases, isLoading } = useOccupancy()
    const { units, isLoading: unitsLoading } = useOccupancyUnits()
    const [viewMode, setViewMode] = useState<ViewMode>('leases')
    const [filter, setFilter] = useState<OccupancyFilter>('all')
    const [boardFilter, setBoardFilter] = useState<OccupancyBoardFilter>(
        'incomplete',
    )
    const [query, setQuery] = useState('')
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
    const [assignLease, setAssignLease] = useState<OccupancyLease | null>(null)
    const [focusUnitId, setFocusUnitId] = useState<number | null>(null)
    const [focusNonce, setFocusNonce] = useState(0)
    const [legendOpen, setLegendOpen] = useState(false)
    const { openPersonFile } = usePersonFile()

    const toggleExpand = (id: number) => {
        setExpandedRows((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const counts = useMemo(() => {
        const unassigned = leases.filter(isUnassignedOccupancyLease).length
        const drafts = leases.filter(
            (l) => l.occupancy_assignment === 'suggested',
        ).length
        const placed = leases.filter(
            (l) => l.area_id !== null && l.occupancy_assignment !== 'suggested',
        ).length
        return { all: leases.length, unassigned, drafts, placed }
    }, [leases])

    const filteredRows = useMemo(() => {
        let base = leases
        if (filter === 'unassigned') {
            base = base.filter(isUnassignedOccupancyLease)
        }
        if (filter === 'drafts') {
            base = base.filter((l) => l.occupancy_assignment === 'suggested')
        }
        if (filter === 'placed') {
            base = base.filter(
                (l) =>
                    l.area_id !== null &&
                    l.occupancy_assignment !== 'suggested',
            )
        }

        if (!query.trim()) return base
        const q = query.toLowerCase()
        return base.filter(
            (l) =>
                l.tenant_name.toLowerCase().includes(q) ||
                l.tenant_email.toLowerCase().includes(q) ||
                (l.unit_name ?? '').toLowerCase().includes(q) ||
                (l.unit_config_name ?? '').toLowerCase().includes(q),
        )
    }, [leases, filter, query])

    const showOnBoard = (lease: OccupancyLease) => {
        if (lease.unit == null) return
        setFocusUnitId(lease.unit)
        setFocusNonce((value) => value + 1)
        setBoardFilter('all')
        setViewMode('bed_spaces')
    }

    const showUnitOnBoard = (unitId: number) => {
        setFocusUnitId(unitId)
        setFocusNonce((value) => value + 1)
        setBoardFilter('all')
        setViewMode('bed_spaces')
    }

    if (isLoading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={8}
            >
                <CircularProgress size={32} />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            {/* View toggle */}
            <Box className={classes.viewToggle}>
                <Box className={classes.viewToggleTabs}>
                    <Chip
                        icon={<ListAltIcon style={{ fontSize: 16 }} />}
                        label="Leases"
                        size="small"
                        clickable
                        color={viewMode === 'leases' ? 'primary' : 'default'}
                        onClick={() => setViewMode('leases')}
                        style={{ fontWeight: 600 }}
                    />
                    <Chip
                        icon={<HotelIcon style={{ fontSize: 16 }} />}
                        label="Bed Spaces"
                        size="small"
                        clickable
                        color={
                            viewMode === 'bed_spaces' ? 'primary' : 'default'
                        }
                        onClick={() => setViewMode('bed_spaces')}
                        style={{ fontWeight: 600 }}
                    />
                </Box>
                <IconButton
                    size="small"
                    aria-label="Occupancy icon legend"
                    onClick={() => setLegendOpen(true)}
                >
                    <InfoOutlinedIcon fontSize="small" />
                </IconButton>
            </Box>

            <OccupancyChrome
                leases={leases}
                units={units}
                viewMode={viewMode}
                leaseFilter={filter}
                boardFilter={boardFilter}
                onUnassigned={() => {
                    setViewMode('leases')
                    setFilter('unassigned')
                }}
                onDrafts={() => {
                    setViewMode('bed_spaces')
                    setBoardFilter('drafts')
                }}
                onOpenBeds={() => {
                    setViewMode('bed_spaces')
                    setBoardFilter('incomplete')
                }}
                onConflicts={() => {
                    setViewMode('bed_spaces')
                    setBoardFilter('conflicts')
                }}
                onMinScore={showUnitOnBoard}
                onSuggested={() => {
                    setFilter('drafts')
                    setBoardFilter('drafts')
                }}
            />

            {viewMode === 'leases' && (
                <>
                    {/* Filter & search */}
                    <Box className={classes.filterRow}>
                        <Box className={classes.viewTabs}>
                            {(Object.keys(
                                FILTER_LABELS,
                            ) as OccupancyFilter[]).map((f) => (
                                <Chip
                                    key={f}
                                    label={`${FILTER_LABELS[f]} (${counts[f]})`}
                                    size="small"
                                    clickable
                                    color={filter === f ? 'primary' : 'default'}
                                    onClick={() => setFilter(f)}
                                />
                            ))}
                        </Box>
                        <TextField
                            className={classes.search}
                            size="small"
                            variant="outlined"
                            placeholder="Search by name, email, or unit"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon
                                            fontSize="small"
                                            color="disabled"
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Leases Table */}
                    <Box className={classes.tableCard}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead className={classes.tableHead}>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>Tenant</TableCell>
                                        <TableCell>Unit</TableCell>
                                        <TableCell>Floor plan</TableCell>
                                        <TableCell>Lifestyle</TableCell>
                                        <TableCell>Facts</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRows.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={8}
                                                align="center"
                                            >
                                                <Box py={4}>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        No leases found.
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredRows.map((lease) => {
                                            const type = getLeaseType(lease)
                                            const isTransfer =
                                                type === 'transfer'
                                            const isExpanded = expandedRows.has(
                                                lease.id,
                                            )
                                            const source = placementSourceLabel(
                                                lease,
                                            )
                                            const canOpenCrm =
                                                lease.lead != null

                                            return (
                                                <React.Fragment key={lease.id}>
                                                    <TableRow
                                                        hover
                                                        className={
                                                            canOpenCrm
                                                                ? classes.clickableRow
                                                                : undefined
                                                        }
                                                        onClick={() => {
                                                            if (
                                                                canOpenCrm &&
                                                                lease.lead !=
                                                                    null
                                                            ) {
                                                                openPersonFile({
                                                                    leadId:
                                                                        lease.lead,
                                                                    userId:
                                                                        lease.tenant,
                                                                })
                                                            }
                                                        }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            {isTransfer && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(
                                                                        event,
                                                                    ) => {
                                                                        event.stopPropagation()
                                                                        toggleExpand(
                                                                            lease.id,
                                                                        )
                                                                    }}
                                                                >
                                                                    {isExpanded ? (
                                                                        <KeyboardArrowUpIcon fontSize="small" />
                                                                    ) : (
                                                                        <KeyboardArrowDownIcon fontSize="small" />
                                                                    )}
                                                                </IconButton>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box
                                                                className={
                                                                    classes.personCell
                                                                }
                                                            >
                                                                <NextActionTableIcon
                                                                    action={resolveOccupancyNextAction(
                                                                        lease,
                                                                    )}
                                                                />
                                                                <Box
                                                                    minWidth={0}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        className={
                                                                            canOpenCrm
                                                                                ? `${classes.personName} ${classes.personNameClickable}`
                                                                                : classes.personName
                                                                        }
                                                                    >
                                                                        {
                                                                            lease.tenant_name
                                                                        }
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="textSecondary"
                                                                    >
                                                                        {
                                                                            lease.tenant_email
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            {lease.unit_name ? (
                                                                <Box>
                                                                    <Typography variant="body2">
                                                                        {
                                                                            lease.unit_name
                                                                        }{' '}
                                                                        -{' '}
                                                                        {lease.area_config_name ??
                                                                            ''}{' '}
                                                                        {lease.area_label ??
                                                                            ''}
                                                                    </Typography>
                                                                    {lease.occupancy_assignment ===
                                                                        'suggested' && (
                                                                        <Chip
                                                                            label="Suggested"
                                                                            size="small"
                                                                            className={
                                                                                classes.vacantChip
                                                                            }
                                                                            style={{
                                                                                marginTop: 4,
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {lease.occupancy_assignment ===
                                                                        'confirmed' && (
                                                                        <Chip
                                                                            label="Confirmed"
                                                                            size="small"
                                                                            className={
                                                                                classes.occupiedChip
                                                                            }
                                                                            style={{
                                                                                marginTop: 4,
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {source && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="textSecondary"
                                                                            display="block"
                                                                        >
                                                                            {
                                                                                source
                                                                            }
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            ) : (
                                                                <Box>
                                                                    <Chip
                                                                        label="Unassigned"
                                                                        size="small"
                                                                        className={
                                                                            classes.unassignedBadge
                                                                        }
                                                                    />
                                                                    {lease.unit_config_name && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="textSecondary"
                                                                            display="block"
                                                                        >
                                                                            {
                                                                                lease.unit_config_name
                                                                            }
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {lease.unit_config_name ||
                                                                    lease.area_config_name ||
                                                                    '—'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={
                                                                    lease.lifestyle_complete
                                                                        ? 'Complete'
                                                                        : 'Incomplete'
                                                                }
                                                                size="small"
                                                                className={
                                                                    lease.lifestyle_complete
                                                                        ? classes.occupiedChip
                                                                        : classes.unassignedBadge
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <OccupancyFactIcons
                                                                lease={lease}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={getLeaseTypeLabel(
                                                                    type,
                                                                )}
                                                                size="small"
                                                                className={
                                                                    type ===
                                                                    'renewal'
                                                                        ? classes.typeRenewal
                                                                        : type ===
                                                                          'transfer'
                                                                        ? classes.typeTransfer
                                                                        : classes.typeNewMoveIn
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Box
                                                                display="flex"
                                                                justifyContent="flex-end"
                                                                style={{
                                                                    gap: 8,
                                                                }}
                                                            >
                                                                {lease.area_id ===
                                                                    null && (
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        startIcon={
                                                                            <AssignmentIndIcon fontSize="small" />
                                                                        }
                                                                        onClick={(
                                                                            event,
                                                                        ) => {
                                                                            event.stopPropagation()
                                                                            setAssignLease(
                                                                                lease,
                                                                            )
                                                                        }}
                                                                        style={{
                                                                            textTransform:
                                                                                'none',
                                                                            fontWeight: 600,
                                                                            fontSize:
                                                                                '0.75rem',
                                                                            borderRadius: 6,
                                                                        }}
                                                                    >
                                                                        Assign
                                                                        unit
                                                                    </Button>
                                                                )}
                                                                {lease.area_id !=
                                                                    null &&
                                                                    !isOccupancyImmovable(
                                                                        lease,
                                                                    ) && (
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            startIcon={
                                                                                <AssignmentIndIcon fontSize="small" />
                                                                            }
                                                                            onClick={(
                                                                                event,
                                                                            ) => {
                                                                                event.stopPropagation()
                                                                                setAssignLease(
                                                                                    lease,
                                                                                )
                                                                            }}
                                                                            style={{
                                                                                textTransform:
                                                                                    'none',
                                                                                fontWeight: 600,
                                                                                fontSize:
                                                                                    '0.75rem',
                                                                                borderRadius: 6,
                                                                            }}
                                                                        >
                                                                            Move
                                                                            to…
                                                                        </Button>
                                                                    )}
                                                                {lease.unit !=
                                                                    null && (
                                                                    <Button
                                                                        size="small"
                                                                        variant="text"
                                                                        color="primary"
                                                                        startIcon={
                                                                            <HotelIcon fontSize="small" />
                                                                        }
                                                                        onClick={(
                                                                            event,
                                                                        ) => {
                                                                            event.stopPropagation()
                                                                            showOnBoard(
                                                                                lease,
                                                                            )
                                                                        }}
                                                                        style={{
                                                                            textTransform:
                                                                                'none',
                                                                            fontWeight: 600,
                                                                            fontSize:
                                                                                '0.75rem',
                                                                        }}
                                                                    >
                                                                        Show on
                                                                        board
                                                                    </Button>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>

                                                    {/* Expandable transfer detail */}
                                                    {isTransfer && (
                                                        <TableRow
                                                            className={
                                                                classes.expandedRow
                                                            }
                                                        >
                                                            <TableCell
                                                                colSpan={8}
                                                                style={{
                                                                    padding: 0,
                                                                }}
                                                            >
                                                                <Collapse
                                                                    in={
                                                                        isExpanded
                                                                    }
                                                                    timeout="auto"
                                                                    unmountOnExit
                                                                >
                                                                    <Box
                                                                        className={
                                                                            classes.transferDetail
                                                                        }
                                                                    >
                                                                        {lease
                                                                            .renewal_offer
                                                                            ?.transfer_desired_unit_config_name && (
                                                                            <Box
                                                                                className={
                                                                                    classes.transferField
                                                                                }
                                                                            >
                                                                                <Typography
                                                                                    className={
                                                                                        classes.transferLabel
                                                                                    }
                                                                                >
                                                                                    Desired
                                                                                    Floor
                                                                                    Plan
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    style={{
                                                                                        fontWeight: 600,
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        lease
                                                                                            .renewal_offer
                                                                                            .transfer_desired_unit_config_name
                                                                                    }
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                        {lease
                                                                            .renewal_offer
                                                                            ?.transfer_roommates &&
                                                                            lease
                                                                                .renewal_offer
                                                                                .transfer_roommates
                                                                                .length >
                                                                                0 && (
                                                                                <Box
                                                                                    className={
                                                                                        classes.transferField
                                                                                    }
                                                                                >
                                                                                    <Typography
                                                                                        className={
                                                                                            classes.transferLabel
                                                                                        }
                                                                                    >
                                                                                        <Box
                                                                                            display="flex"
                                                                                            alignItems="center"
                                                                                            style={{
                                                                                                gap: 4,
                                                                                            }}
                                                                                        >
                                                                                            <PeopleIcon
                                                                                                style={{
                                                                                                    fontSize: 14,
                                                                                                }}
                                                                                            />
                                                                                            Roommates
                                                                                            (
                                                                                            {
                                                                                                lease
                                                                                                    .renewal_offer
                                                                                                    .transfer_roommates
                                                                                                    .length
                                                                                            }

                                                                                            )
                                                                                        </Box>
                                                                                    </Typography>
                                                                                    <Box
                                                                                        display="flex"
                                                                                        flexWrap="wrap"
                                                                                        mt={
                                                                                            0.5
                                                                                        }
                                                                                    >
                                                                                        {lease.renewal_offer.transfer_roommates.map(
                                                                                            (
                                                                                                rm,
                                                                                            ) => (
                                                                                                <Chip
                                                                                                    key={
                                                                                                        rm.user_id
                                                                                                    }
                                                                                                    label={
                                                                                                        rm.name
                                                                                                    }
                                                                                                    size="small"
                                                                                                    className={
                                                                                                        classes.roommateChip
                                                                                                    }
                                                                                                    style={{
                                                                                                        backgroundColor:
                                                                                                            '#fef3c7',
                                                                                                        color:
                                                                                                            '#92400e',
                                                                                                        fontWeight: 600,
                                                                                                        fontSize:
                                                                                                            '0.75rem',
                                                                                                        height: 24,
                                                                                                        borderRadius: 6,
                                                                                                    }}
                                                                                                />
                                                                                            ),
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}
                                                                        {lease
                                                                            .renewal_offer
                                                                            ?.transfer_notes && (
                                                                            <Box
                                                                                className={
                                                                                    classes.transferField
                                                                                }
                                                                            >
                                                                                <Typography
                                                                                    className={
                                                                                        classes.transferLabel
                                                                                    }
                                                                                >
                                                                                    Additional
                                                                                    Notes
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    style={{
                                                                                        whiteSpace:
                                                                                            'pre-wrap',
                                                                                        backgroundColor:
                                                                                            '#fff',
                                                                                        border:
                                                                                            '1px solid #fde68a',
                                                                                        borderRadius: 8,
                                                                                        padding:
                                                                                            '8px 12px',
                                                                                        lineHeight: 1.5,
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        lease
                                                                                            .renewal_offer
                                                                                            .transfer_notes
                                                                                    }
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </React.Fragment>
                                            )
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </>
            )}

            {viewMode === 'bed_spaces' && (
                <OccupancyBedBoard
                    leases={leases}
                    units={units}
                    unitsLoading={unitsLoading}
                    focusUnitId={focusUnitId}
                    focusNonce={focusNonce}
                    boardFilter={boardFilter}
                    onBoardFilterChange={setBoardFilter}
                />
            )}

            <OccupancyAssignDialog
                open={assignLease != null}
                lease={assignLease}
                units={units}
                leases={leases}
                title={
                    assignLease?.area_id != null ? 'Move to…' : 'Assign unit'
                }
                onClose={() => setAssignLease(null)}
            />
            <OccupancyIconLegendDialog
                open={legendOpen}
                onClose={() => setLegendOpen(false)}
            />
        </Box>
    )
}
